import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './UserDashboard.css';

const ProfileEditModal = ({ 
  isOpen, 
  onClose, 
  user, 
  updateUser, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    countryCode: 'us', // Default country code for PhoneInput
    dateOfBirth: '',
    professionalTitle: '',
    bio: '',
    country: '',
    timezone: '',
    timezoneDisplay: '',
    workType: '',
    jobSeekingStatus: ''
  });
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [workTypeOptions, setWorkTypeOptions] = useState([
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'contract', label: 'Contract' }
  ]);
  const [jobSeekingStatusOptions] = useState([
    { value: 'actively-seeking', label: 'Actively Seeking' },
    { value: 'open-to-opportunities', label: 'Open to Opportunities' },
    { value: 'not-looking', label: 'Not Looking' },
    { value: 'employed-seeking', label: 'Employed but Seeking' }
  ]);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);

  // Fetch timezone and country options on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch timezones
        const timezoneResponse = await ApiService.get('/timezones');
        if (timezoneResponse.success) {
          setTimezoneOptions(timezoneResponse.data);
        }
        
        // Fetch countries
        const countryResponse = await ApiService.get('/countries');
        if (countryResponse.success) {
          setCountryOptions(countryResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    
    fetchOptions();
  }, []);

  // Separate useEffect for when modal first opens
  useEffect(() => {
    if (isOpen) {
      // Clear success message and errors when modal first opens
      setSuccessMessage('');
      setShowSuccessMessage(false);
      setShowSuccessIcon(false);
      setErrors({});
      
      // Get user data from props or localStorage as fallback
      const userData = user || JSON.parse(localStorage.getItem('auth_user') || '{}');
      
      // Format date for HTML date input (YYYY-MM-DD)
      let formattedDate = '';
      if (userData.date_of_birth) {
        const date = new Date(userData.date_of_birth);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString().split('T')[0];
        }
      }
      
      const formattedPhone = formatExistingPhone(userData.phone || '');
      // Extract country code from phone number if available
      let countryCode = 'us'; // default
      if (userData.phone && userData.phone.startsWith('+')) {
        // Try to detect country code from phone number
        // This is a simplified approach - PhoneInput will handle it better if we pass the full number
        const phoneParts = userData.phone.match(/^\+(\d{1,3})(.*)$/);
        if (phoneParts && phoneParts[1]) {
          // Map common country codes to PhoneInput country codes
          const countryCodeMap = {
            '1': 'us',
            '44': 'gb',
            '33': 'fr',
            '49': 'de',
            '91': 'in',
            '86': 'cn',
            '81': 'jp',
            '7': 'ru',
            '61': 'au',
            '55': 'br',
          };
          countryCode = countryCodeMap[phoneParts[1]] || 'us';
        }
      }
      
      // Split name into firstName and lastName
      const nameParts = (userData.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const formDataToSet = {
        firstName: formatName(firstName),
        lastName: formatName(lastName),
        phone: formattedPhone,
        countryCode: countryCode,
        dateOfBirth: formattedDate,
        professionalTitle: userData.professional_title || '',
        bio: userData.bio || '',
        country: userData.country_code || '',
        timezone: userData.timezone || '',
        timezoneDisplay: userData.timezone_display || '',
        workType: userData.work_type || '',
        jobSeekingStatus: userData.job_seeking_status || ''
      };
      setFormData(formDataToSet);
    }
  }, [isOpen]); // Only depend on isOpen, not user

  // Format existing phone number when loading - PhoneInput handles + prefix automatically
  const formatExistingPhone = (phone) => {
    if (!phone) return '';
    
    // PhoneInput can handle both formats, but we'll keep it as is
    // The value from onChange will include + prefix
    return phone;
  };

  // Name formatting function - capitalize first letter of each word
  const formatName = (value) => {
    if (!value) return '';
    
    return value
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Format firstName and lastName - capitalize first letter of each word
    if (name === 'firstName' || name === 'lastName') {
      processedValue = formatName(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear success message and icon when user starts making changes
    if (showSuccessMessage) {
      setShowSuccessMessage(false);
      setSuccessMessage('');
      setShowSuccessIcon(false);
    }
  };

  const handlePhoneChange = (value, country) => {
    // Ensure phone number starts with + if it doesn't already
    let formattedPhone = value;
    if (value && !value.startsWith('+')) {
      formattedPhone = '+' + value;
    }
    
    setFormData(prev => ({
      ...prev,
      phone: formattedPhone,
      countryCode: country ? country.countryCode : 'us'
    }));
    
    // Clear error when user starts typing
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }));
    }
    
    // Clear success message and icon when user starts making changes
    if (showSuccessMessage) {
      setShowSuccessMessage(false);
      setSuccessMessage('');
      setShowSuccessIcon(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    
    try {
      // Prepare JSON data - combine firstName and lastName into name
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      
      // Ensure phone is in international format (+country_code+number)
      let phoneToSend = formData.phone || '';
      if (phoneToSend && !phoneToSend.startsWith('+')) {
        phoneToSend = '+' + phoneToSend;
      }
      
      const dataToSend = {
        name: fullName,
        phone: phoneToSend || null, // Send null if empty
        date_of_birth: formData.dateOfBirth,
        professional_title: formData.professionalTitle,
        bio: formData.bio,
        country_code: formData.country,
        timezone: formData.timezone,
        timezone_display: formData.timezoneDisplay, // Send the display name
        work_type: formData.workType,
        job_seeking_status: formData.jobSeekingStatus
      };

      const token = ApiService.getToken();
      
      if (!token) {
        setErrors({ general: 'You are not logged in. Please log in again.' });
        setIsLoading(false);
        return;
      }
      
      // Use ApiService's PUT method for cleaner code
      const response = await ApiService.put('/profile', dataToSend);
      
      if (response.success) {
        updateUser(response.data.user);
        setErrors({});
        
        // Show success message
        const successMsg = '✅ Profile updated successfully! Your information has been saved.';
        showSuccess(successMsg);
        
        // Call success callback
        if (onSuccess) {
          onSuccess(successMsg);
        }
      } else {
        setErrors({ general: response.message || 'Profile update failed. Please check your information and try again.' });
      }

    } catch (error) {
      console.error('Profile update error:', error);
      
      if (error.status === 422 && error.errors) {
        // Handle validation errors
        const formattedErrors = {};
        Object.keys(error.errors).forEach(key => {
          if (Array.isArray(error.errors[key])) {
            formattedErrors[key] = error.errors[key][0];
          } else {
            formattedErrors[key] = error.errors[key];
          }
        });
        setErrors(formattedErrors);
      } else if (error.status === 401) {
        setErrors({ general: 'Your session has expired. Please log in again.' });
      } else if (error.name === 'NetworkError' || error.message.includes('Network')) {
        setErrors({ general: 'Network error. Please check your internet connection and try again.' });
      } else if (error.name === 'TimeoutError') {
        setErrors({ general: 'Request timed out. Please try again.' });
      } else if (error.message.includes('Failed to fetch')) {
        setErrors({ general: 'Cannot connect to server. Please make sure the backend is running on port 3000.' });
      } else {
        setErrors({ general: `An unexpected error occurred: ${error.message}. Please try again.` });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const showSuccess = (message) => {
    // Clear any existing errors first
    setErrors({});
    
    // Set success message with a small delay to ensure proper rendering
    setTimeout(() => {
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      setShowSuccessIcon(true);
    }, 100);
  };

  const hideSuccess = () => {
    setShowSuccessMessage(false);
    setSuccessMessage('');
    setShowSuccessIcon(false);
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>Edit Profile Information</h2>
            {showSuccessMessage && (
              <div className="header-success-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
                <span>Profile Updated Successfully!</span>
              </div>
            )}
          </div>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          
          {errors.general && (
            <div className="error-message general-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <div style={{ flex: 1 }}>
                <div>{errors.general}</div>
              </div>
              <button 
                type="button" 
                onClick={() => setErrors({})}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'inherit', 
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Dismiss error"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}


          <form id="profile-edit-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-firstName">First Name</label>
                <input
                  type="text"
                  id="modal-firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={errors.firstName || errors.name ? 'error' : ''}
                  placeholder="First name"
                  disabled={isLoading}
                />
                {(errors.firstName || errors.name) && <span className="error-text">{errors.firstName || errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="modal-lastName">Last Name</label>
                <input
                  type="text"
                  id="modal-lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={errors.lastName || errors.name ? 'error' : ''}
                  placeholder="Last name"
                  disabled={isLoading}
                />
                {(errors.lastName || errors.name) && <span className="error-text">{errors.lastName || errors.name}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-phone">Phone Number <span className="optional-text">(Optional)</span></label>
                <div className="phone-input-container">
                  <PhoneInput
                    country={formData.countryCode || "us"}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    disabled={isLoading}
                    inputProps={{
                      name: 'phone',
                      required: false,
                      placeholder: "Enter your phone number (optional)"
                    }}
                    enableSearch={true}
                    searchPlaceholder="Search countries..."
                    specialLabel=""
                    autoFormat={true}
                    disableCountryCode={false}
                  />
                </div>
                {errors.phone && <span className="error-text">{errors.phone}</span>}
                <div className="field-help-text">
                  You can add and verify your phone number later from your dashboard
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="modal-dateOfBirth">Date of Birth</label>
              <input
                type="date"
                id="modal-dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className={errors.date_of_birth ? 'error' : ''}
                disabled={isLoading}
              />
              {formData.dateOfBirth && (
                <p className="age-display">Age: {calculateAge(formData.dateOfBirth)} years</p>
              )}
              {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={user?.email || JSON.parse(localStorage.getItem('auth_user') || '{}').email || ''}
                disabled
                className={`email-input ${!user?.is_email_verified && !JSON.parse(localStorage.getItem('auth_user') || '{}').is_email_verified ? 'unverified-highlight' : 'verified-input'}`}
              />
              <div className="email-status">
                {(user?.is_email_verified || JSON.parse(localStorage.getItem('auth_user') || '{}').is_email_verified) ? (
                  <div className="email-verified-badge">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22,4 12,14.01 9,11.01"></polyline>
                    </svg>
                    <div className="email-status-text">
                      <strong>Email Verified</strong>
                      <span>Your email address has been successfully verified</span>
                    </div>
                  </div>
                ) : (
                  <div className="email-unverified-badge">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <div className="email-status-text">
                      <strong>Email Not Verified</strong>
                      <span>Please verify your email to access all features</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="modal-professionalTitle">Professional Title / Headline</label>
              <input
                type="text"
                id="modal-professionalTitle"
                name="professionalTitle"
                value={formData.professionalTitle}
                onChange={handleChange}
                className={errors.professional_title ? 'error' : ''}
                placeholder="e.g., Full-Stack Developer | React + Node.js"
                disabled={isLoading}
              />
              {errors.professional_title && <span className="error-text">{errors.professional_title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="modal-bio">Short Bio / About Me</label>
              <textarea
                id="modal-bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className={errors.bio ? 'error' : ''}
                placeholder="Tell us about your skills, experience, and career goals..."
                rows="4"
                disabled={isLoading}
              />
              {errors.bio && <span className="error-text">{errors.bio}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-country">Country</label>
                <Select
                  id="modal-country"
                  name="country"
                  value={countryOptions.find(option => option.value === formData.country) || null}
                  onChange={(selectedOption) => {
                    handleChange({
                      target: {
                        name: 'country',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                  }}
                  options={countryOptions}
                  placeholder="Select country"
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={isLoading}
                  className={`react-select-container ${errors.country_code ? 'error' : ''} ${isLoading ? 'loading' : ''}`}
                  classNamePrefix="react-select"
                />
                {errors.country_code && <span className="error-text">{errors.country_code}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="modal-timezone">Preferred Work Timezone</label>
                <Select
                  id="modal-timezone"
                  name="timezone"
                  value={timezoneOptions.find(option => option.value === formData.timezone) || null}
                  onChange={(selectedOption) => {
                    handleChange({
                      target: {
                        name: 'timezone',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                    // Also set the display name
                    setFormData(prev => ({
                      ...prev,
                      timezoneDisplay: selectedOption ? selectedOption.label : ''
                    }));
                  }}
                  options={timezoneOptions}
                  placeholder="Select preferred work timezone"
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={isLoading}
                  className={`react-select-container ${errors.timezone ? 'error' : ''} ${isLoading ? 'loading' : ''}`}
                  classNamePrefix="react-select"
                />
                <small style={{color: '#6b7280', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                  Choose the timezone you prefer to work in (can be different from your country)
                </small>
                {errors.timezone && <span className="error-text">{errors.timezone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="modal-workType">Preferred Work Type</label>
              <Select
                id="modal-workType"
                name="workType"
                value={workTypeOptions.find(option => option.value === formData.workType) || null}
                onChange={(selectedOption) => {
                  handleChange({
                    target: {
                      name: 'workType',
                      value: selectedOption ? selectedOption.value : ''
                    }
                  });
                }}
                options={workTypeOptions}
                placeholder="Select work type"
                isSearchable={true}
                isClearable={true}
                isDisabled={isLoading}
                className={`react-select-container ${errors.work_type ? 'error' : ''} ${isLoading ? 'loading' : ''}`}
                classNamePrefix="react-select"
              />
              {errors.work_type && <span className="error-text">{errors.work_type}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="modal-jobSeekingStatus">Job Seeking Status</label>
              <Select
                id="modal-jobSeekingStatus"
                name="jobSeekingStatus"
                value={jobSeekingStatusOptions.find(option => option.value === formData.jobSeekingStatus) || null}
                onChange={(selectedOption) => {
                  handleChange({
                    target: {
                      name: 'jobSeekingStatus',
                      value: selectedOption ? selectedOption.value : ''
                    }
                  });
                }}
                options={jobSeekingStatusOptions}
                placeholder="Select your job seeking status"
                isSearchable={true}
                isClearable={true}
                isDisabled={isLoading}
                className={`react-select-container ${errors.job_seeking_status ? 'error' : ''} ${isLoading ? 'loading' : ''}`}
                classNamePrefix="react-select"
              />
              {errors.job_seeking_status && <span className="error-text">{errors.job_seeking_status}</span>}
            </div>

          </form>
        </div>

        <div className="modal-form-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {showSuccessMessage ? 'Close' : 'Cancel'}
          </button>
          <button 
            type="submit" 
            form="profile-edit-form"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Updating...
              </>
            ) : showSuccessIcon ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
                Save Changes
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditModal;
